import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FormPageLayout } from '@components/common';
import ReservationForm from '@components/common/forms/ReservationForm';
import { useLanguage } from '@contexts/LanguageContext';
import { useNotification } from '@hooks/useNotification';
import api from '@services/api';

export const ResourceReservePage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const notify = useNotification();

    const [resource, setResource] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchResource = async () => {
            if (!id) return;

            try {
                setLoading(true);
                const response = await api.get(`/resources/${id}`);
                setResource(response.data);
                setError(null);
            } catch (err) {
                console.error('Failed to fetch resource:', err);
                setError(t('resources.resourceReserve.fetchError'));
            } finally {
                setLoading(false);
            }
        };

        fetchResource();
    }, [id, t]);

    const handleSubmit = async (formData) => {
        try {
            // Map form data to borrowing API structure for reservations
            const submitData = {
                reserved_from: formData.reserved_from,
                reserved_until: formData.reserved_until,
                reservation_notes: formData.reservation_notes || null,
                borrowed_at: null, // Makes it a reservation, not an actual borrowing
                resource_id: id,
                dynamic_fields: formData.dynamic_fields || {}
            };

            const response = await api.borrowings.create(submitData);
            notify.success(t('reservation.created_successfully'));
            navigate(`/borrowings/${response.data.id}`);
        } catch (err) {
            console.error('Failed to reserve resource:', err);
            notify.error(t('resources.resourceReserve.reserveError'));
            throw err;
        }
    };

    const handleCancel = () => {
        navigate(`/resources/${id}`);
    };

    if (loading) {
        return (
            <FormPageLayout
                title={t('resources.resourceReserve.reserveResource')}
                subtitle={t('resources.resourceReserve.loading')}
                onBack={handleCancel}
                loading={true}
            />
        );
    }

    if (error || !resource) {
        return (
            <FormPageLayout
                title={t('resources.resourceReserve.reserveResource')}
                subtitle={error || t('resources.resourceReserve.notFound')}
                onBack={handleCancel}
                error={true}
            />
        );
    }

    return (
        <FormPageLayout
            title={t('resources.resourceReserve.reserveResource')}
            subtitle={t('resources.resourceReserve.subtitle', { resourceName: resource.name })}
            onBack={handleCancel}
        >
            <ReservationForm
                resource={resource}
                isEditing={false}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
            />
        </FormPageLayout>
    );
};

export default ResourceReservePage;
